import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'
import { ApiError } from '@/shared/api/http-client'
import { createPremiumInvoice, type PaymentProvider, type PremiumPurpose } from '@/features/account/api/account-api'

export interface PremiumOption {
  purpose: PremiumPurpose
  title: string
  description: string
  providers: Record<PaymentProvider, { months: number; amount: number }[]>
}

interface PremiumPaymentModalProps {
  options: PremiumOption[]
  isLoading?: boolean
  onClose: () => void
}

export function PremiumPaymentModal({ options, isLoading, onClose }: PremiumPaymentModalProps) {
  const { t } = useTranslation()
  const [selectedPurpose, setSelectedPurpose] = useState<PremiumPurpose>('Premium1')
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>('CryptoPay')
  const [selectedMonths, setSelectedMonths] = useState<number>(1)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const activePremium = useMemo(
    () => options.find((option) => option.purpose === selectedPurpose),
    [options, selectedPurpose],
  )

  const activeProviderOptions = useMemo(() => {
    if (!activePremium) return []
    return activePremium.providers[selectedProvider] ?? []
  }, [activePremium, selectedProvider])

  useEffect(() => {
    if (!activeProviderOptions.length) return
    setSelectedMonths((current) =>
      activeProviderOptions.some((item) => item.months === current) ? current : activeProviderOptions[0].months,
    )
  }, [activeProviderOptions])

  const invoiceMutation = useMutation({
    mutationFn: createPremiumInvoice,
  })

  const handleCreateInvoice = async () => {
    setError(null)
    setStatus(null)
    if (!activePremium) {
      setError(t('premiumModal.error_no_option'))
      return
    }
    const option = activeProviderOptions.find((item) => item.months === selectedMonths)
    if (!option) {
      setError(t('premiumModal.error_no_months'))
      return
    }

    try {
      const response = await invoiceMutation.mutateAsync({
        provider: selectedProvider,
        amount: option.amount,
        purpose: selectedPurpose,
      })
      if (response?.invoiceLink) {
        setStatus(t('premiumModal.status_invoice_created'))
        if (typeof window !== 'undefined') {
          window.open(response.invoiceLink, '_blank', 'noopener')
        }
      } else {
        setError(t('premiumModal.error_invoice_generic'))
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('premiumModal.error_unknown')
      setError(message)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute right-6 top-6 z-[101] size-12 rounded-full border border-white/15 bg-white/10 text-2xl text-white/80 backdrop-blur hover:bg-white/15"
        onClick={onClose}
        aria-label={t('premiumModal.close_aria')}
      >
        ×
      </button>

      <div
        className="relative w-full max-w-5xl rounded-3xl border border-white/10 bg-surface-muted/90 p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex-1 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-primary-soft/80">
                {t('premiumModal.badge')}
              </p>
              <h2 className="text-xl font-semibold text-white">{t('premiumModal.title')}</h2>
              <p className="text-sm text-slate-400">{t('premiumModal.subtitle')}</p>
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              {options.map((option) => {
                const isActive = selectedPurpose === option.purpose
                return (
                  <button
                    key={option.purpose}
                    type="button"
                    className={cn(
                      'group rounded-2xl border p-4 text-left transition',
                      isActive
                        ? 'border-primary/70 bg-primary/15 text-white shadow-[0_18px_40px_-30px_rgba(255,46,166,0.8)]'
                        : 'border-white/10 bg-white/5 text-slate-200 hover:border-primary/60 hover:bg-primary/10',
                    )}
                    onClick={() => setSelectedPurpose(option.purpose)}
                    disabled={isLoading}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-[0.2em] text-primary-soft/80">
                          {option.purpose === 'Premium1'
                            ? t('premiumModal.tier_basic')
                            : t('premiumModal.tier_plus')}
                        </p>
                        <h3 className="text-base font-semibold text-white">{option.title}</h3>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-slate-300">{option.description}</p>
                  </button>
                )
              })}
            </div>

            <div className="space-y-3 rounded-2xl border border-white/10 bg-surface-muted/70 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-primary-soft/80">
                {t('premiumModal.payment_method_label')}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={cn(
                    'rounded-xl border px-3 py-2 text-sm',
                    selectedProvider === 'CryptoPay'
                      ? 'border-primary/70 bg-primary/15 text-white'
                      : 'border-white/10 bg-white/5 text-slate-200 hover:border-white/20',
                  )}
                  onClick={() => setSelectedProvider('CryptoPay')}
                  disabled={isLoading}
                >
                  {t('premiumModal.payment_method_ton')}
                </button>
                <button
                  type="button"
                  className={cn(
                    'rounded-xl border px-3 py-2 text-sm',
                    selectedProvider === 'CryptoPay'
                      ? 'border-white/10 bg-white/5 text-slate-400'
                      : 'border-primary/70 bg-primary/15 text-white',
                  )}
                  onClick={() => setError(t('premiumModal.payment_method_card_unavailable'))}
                  disabled={isLoading}
                >
                  {t('premiumModal.payment_method_card_soon')}
                </button>
              </div>
              <p className="text-xs text-slate-400">{t('premiumModal.payment_method_ton_note')}</p>
            </div>

            <div className="space-y-3 rounded-2xl border border-white/10 bg-surface-muted/70 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-primary-soft/80">
                {t('premiumModal.duration_label')}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {activeProviderOptions.map((option) => (
                  <button
                    key={option.months}
                    type="button"
                    className={cn(
                      'rounded-xl border px-3 py-2 text-sm',
                      selectedMonths === option.months
                        ? 'border-primary/70 bg-primary/15 text-white'
                        : 'border-white/10 bg-white/5 text-slate-200 hover:border-white/20',
                    )}
                    onClick={() => setSelectedMonths(option.months)}
                    disabled={isLoading}
                  >
                    {formatMonths(option.months, t)}
                  </button>
                ))}
                {!activeProviderOptions.length ? (
                  <span className="text-xs text-slate-500">
                    {t('premiumModal.no_options_hint')}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="w-full max-w-[480px] rounded-2xl border border-primary/40 bg-gradient-to-br from-white/10 via-surface to-surface-muted/80 p-5 shadow-[0_20px_60px_-35px_rgba(255,46,166,0.7)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-primary-soft/80">
                  {t('premiumModal.selection_label')}
                </p>
                <h3 className="text-lg font-semibold text-white">
                  {activePremium?.title ?? t('premiumModal.selection_title_fallback')}
                </h3>
              </div>
              <span className="rounded-full bg-primary/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-soft">
                {selectedProvider === 'CryptoPay'
                  ? t('premiumModal.selection_chip_ton')
                  : t('premiumModal.selection_chip_card')}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-400">
              {activePremium?.description ?? t('premiumModal.selection_description_fallback')}
            </p>

            <div className="mt-4 space-y-3 rounded-xl border border-white/10 bg-surface-muted/60 p-4">
              <Row
                label={t('premiumModal.row_method')}
                value={selectedProvider === 'CryptoPay' ? t('premiumModal.row_method_value_ton') : t('premiumModal.row_method_value_card')}
              />
              <Row
                label={t('premiumModal.row_duration')}
                value={formatMonths(selectedMonths, t)}
              />
              <Row
                label={t('premiumModal.row_amount')}
                value={renderPriceLabel(activeProviderOptions, selectedMonths, t)}
                emphasize
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => {
                  void handleCreateInvoice()
                }}
                disabled={invoiceMutation.isPending || !activeProviderOptions.length}
                className="w-full justify-center md:w-auto"
              >
                {invoiceMutation.isPending
                  ? t('premiumModal.pay_button_pending')
                  : t('premiumModal.pay_button_label')}
              </Button>
            </div>

            {status ? <Pill tone="success">{status}</Pill> : null}
            {error ? <Pill tone="error">{error}</Pill> : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function renderPriceLabel(
  options: { months: number; amount: number }[],
  selectedMonths: number,
  t: (key: string, vars?: Record<string, string | number>) => string,
) {
  const option = options.find((item) => item.months === selectedMonths)
  if (!option) return '-'
  return t('premiumModal.price_label', { amount: option.amount.toString() })
}

function formatMonths(months: number, t: (key: string, vars?: Record<string, string | number>) => string) {
  if (months === 1) return t('premiumModal.months_one')
  if (months >= 2 && months <= 4) return t('premiumModal.months_few', { count: months })
  return t('premiumModal.months_many', { count: months })
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-slate-300">{label}</span>
      <span className={cn('text-sm font-semibold text-white', emphasize && 'text-lg text-primary-soft')}>{value}</span>
    </div>
  )
}

function Pill({ children, tone }: { children: ReactNode; tone: 'success' | 'error' }) {
  return (
    <span
      className={cn(
        'mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
        tone === 'success' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-rose-500/15 text-rose-200',
      )}
    >
      {children}
    </span>
  )
}
